import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ThemeContext, useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { formatUTCDay } from 'utils/analytics/date'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import { FlexRowCenter } from '../styleds'
import * as S from './styleds'
import { SnapshotType } from './types' // Ensure this type is correctly imported
import { filterDataByTimeframe, selectTimeFormat } from './utils/chartHelpers'

type VolumeViewProps = {
  unit: 'both' | 'token0' | 'token1'
  timeframe: '7' | '30' | '90' | 'max'
  snapshots: SnapshotType[]
}

const VolumeView: React.FC<VolumeViewProps> = ({ unit, timeframe, snapshots }) => {
  const theme = useContext(ThemeContext)
  const [filteredData, setFilteredData] = useState<SnapshotType[]>([])

  useEffect(() => {
    const newFilteredData = filterDataByTimeframe(snapshots, timeframe)
    setFilteredData(newFilteredData)
  }, [timeframe, snapshots])

  const dailyVolumeData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return []

    // Create a map to group by date
    const volumeByDay = new Map<number, DailyVolumeData>()

    filteredData.forEach((snapshot) => {
      const timestamp = Number(snapshot.timestamp)
      // Convert timestamp to start of day timestamp (midnight UTC)
      const dayTimestamp = Math.floor(timestamp / 86400) * 86400
      const dateStr = formatUTCDay(dayTimestamp)

      // Get or initialize the data for this day
      const currentData = volumeByDay.get(dayTimestamp) || {
        timestamp: dayTimestamp,
        volumeUSD: 0,
        date: dateStr,
      }

      // Add this snapshot's volume to the daily total
      currentData.volumeUSD += parseFloat(snapshot.volumeUSD || '0')

      // Update the map
      volumeByDay.set(dayTimestamp, currentData)
    })

    // Convert map to array and sort by timestamp
    return Array.from(volumeByDay.values()).sort((a, b) => a.timestamp - b.timestamp)
  }, [filteredData])

  const maxVolumeUSD = Math.max(...dailyVolumeData.map((data) => data.volumeUSD), 0)

  const xAxisFormatter = selectTimeFormat(timeframe)

  const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
    theme,
    xAxisFormatter,
    activeToken,
  }) => {
    if (active && payload && payload.length) {
      return (
        <S.StrategyTooltipFlex>
          <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textSecondary}>
            {xAxisFormatter(payload[0].payload?.timestamp)}
          </ThemedText.DeprecatedBody>
          <FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="700" lineHeight="20px" color={theme.textPrimary}>
              {formatDollarAmount(payload[0].value)}
            </ThemedText.DeprecatedBody>
          </FlexRowCenter>
        </S.StrategyTooltipFlex>
      )
    }
    return null
  }

  const CustomTickLabel = ({
    x,
    y,
    payload,
    axisType,
  }: {
    x?: number
    y?: number
    payload?: any
    axisType?: 'xAxis' | 'yAxis'
  }) => {
    const { textSecondary } = useTheme()

    if (axisType === 'xAxis') {
      return (
        <text x={x} y={y} dy={10} textAnchor="middle" fill={textSecondary} fontSize={12}>
          {formatUTCDay(payload.value, 'MMM D')}
        </text>
      )
    }

    // Y-axis remains the same
    return (
      <text x={x} y={y} dy={0} dx={-14} textAnchor="middle" fill={textSecondary} fontSize={12}>
        {formatDollarAmount(payload.value)}
      </text>
    )
  }

  return (
    <S.GraphFlex style={{ overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={182} minWidth="100%">
        <BarChart data={dailyVolumeData} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
          <XAxis
            dataKey="timestamp"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomTickLabel axisType="xAxis" />}
            minTickGap={5}
          />
          <YAxis
            dataKey="volumeUSD"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomTickLabel axisType="yAxis" />}
            minTickGap={2}
          />
          <Tooltip content={<CustomTooltip theme={theme} xAxisFormatter={xAxisFormatter} />} />
          <Bar dataKey="volumeUSD" fill="#3083DC" />
        </BarChart>
      </ResponsiveContainer>
    </S.GraphFlex>
  )
}

export default VolumeView
